import type { CSSProperties, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { Plan } from '@/api/types'
import { CheckIcon } from '@/components/ui/icons'
import { useSettingsStore } from '@/stores/settingsStore'

interface PlanSelectorProps {
  plans: Plan[]
  selectedPlanCode?: string | null
  currentPlanCode?: string | null
  title?: string
  subtitle?: string
  note?: string
  loading?: boolean
  onSelect?: (plan: Plan) => void
  renderAction?: (plan: Plan, state: PlanCardState) => ReactNode
}

interface PlanCardState {
  isSelected: boolean
  isCurrent: boolean
  isPopular: boolean
}

interface PlanFeature {
  label: string
}

const shellStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 28,
}

const cardsStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
  alignItems: 'stretch',
  width: '100%',
  maxWidth: 980,
  margin: '0 auto',
}

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'clamp(30px, 5.2vw, 56px)',
  lineHeight: 1.05,
  color: 'var(--text)',
  textAlign: 'center',
}

const subtitleStyle: CSSProperties = {
  margin: '0 auto',
  maxWidth: 760,
  fontSize: 15,
  lineHeight: 1.55,
  color: 'var(--text-muted)',
  textAlign: 'center',
}

const skeletonCardStyle: CSSProperties = {
  minHeight: 520,
  borderRadius: 24,
  border: '1px solid var(--border)',
  background: 'var(--bg-card)',
  padding: 28,
}

export function PlanSelector({
  plans,
  selectedPlanCode,
  currentPlanCode,
  title,
  subtitle,
  note,
  loading = false,
  onSelect,
  renderAction,
}: PlanSelectorProps) {
  const { t, i18n } = useTranslation()
  const resolvedTheme = useSettingsStore((s) => s.resolvedTheme)
  const sortedPlans = sortPlans(plans)

  return (
    <section style={shellStyle}>
      {title || subtitle ? (
        <header style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {title ? (
            <h2 style={titleStyle}>
              {highlightPlanWord(title)}
            </h2>
          ) : null}
          {subtitle ? <p style={subtitleStyle}>{subtitle}</p> : null}
          {note ? (
            <p style={{ ...subtitleStyle, fontSize: 13, fontStyle: 'italic', opacity: 0.45, marginTop: -4 }}>
              {note}
            </p>
          ) : null}
        </header>
      ) : null}

      <div style={cardsStyle}>
        {loading
          ? Array.from({ length: 3 }).map((_, index) => (
              <div key={index} style={skeletonCardStyle} aria-hidden="true" />
            ))
          : sortedPlans.map((plan) => {
              const state: PlanCardState = {
                isSelected: selectedPlanCode === plan.code,
                isCurrent: currentPlanCode === plan.code,
                isPopular: plan.code.toLowerCase() === 'pro',
              }

              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  state={state}
                  locale={i18n.language}
                  resolvedTheme={resolvedTheme}
                  onSelect={onSelect}
                  action={renderAction?.(plan, state)}
                  popularLabel={t('billing.planSelector.popular', 'Mais popular')}
                />
              )
            })}
      </div>
    </section>
  )
}

function PlanCard({
  plan,
  state,
  locale,
  resolvedTheme,
  onSelect,
  action,
  popularLabel,
}: {
  plan: Plan
  state: PlanCardState
  locale: string
  resolvedTheme: 'light' | 'dark'
  onSelect?: (plan: Plan) => void
  action?: ReactNode
  popularLabel: string
}) {
  const { t } = useTranslation()
  const features = buildPlanFeatures(plan, t)
  const interactive = Boolean(onSelect)
  const accent = getPlanAccent(plan.code, resolvedTheme)
  const normalizedCode = plan.code.toLowerCase()
  const hasAccentBorder = normalizedCode === 'pro' || normalizedCode === 'team'
  const isEmphasized = state.isPopular || state.isSelected || state.isCurrent

  return (
    <article
      onClick={interactive ? () => onSelect?.(plan) : undefined}
      style={{
        position: 'relative',
        minHeight: state.isPopular ? 520 : 480,
        borderRadius: 24,
        padding: 22,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        cursor: interactive ? 'pointer' : 'default',
        border: `1px solid ${isEmphasized ? accent.borderStrong : hasAccentBorder ? accent.borderSoft : 'var(--border)'}`,
        background: 'var(--bg-card)',
        boxShadow: isEmphasized
          ? `0 0 0 1px ${accent.borderSoft}, 0 24px 50px ${accent.glow}`
          : hasAccentBorder
            ? `0 0 0 1px ${accent.borderSoft}, var(--shadow-card)`
          : 'var(--shadow-card)',
        overflow: 'hidden',
        transform: state.isPopular ? 'scale(1.03)' : 'none',
        zIndex: state.isPopular ? 2 : 1,
      }}
    >
      {state.isPopular ? (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -2px)',
            padding: '9px 20px',
            borderBottomLeftRadius: 14,
            borderBottomRightRadius: 14,
            background: 'linear-gradient(90deg, #A855F7 0%, #22D3EE 100%)',
            color: '#F8FBFF',
            fontSize: 12,
            fontWeight: 700,
            boxShadow: '0 10px 30px rgba(88,28,135,.35)',
          }}
        >
          {popularLabel}
        </div>
      ) : null}

      <div
        style={{
          width: 68,
          height: 68,
          borderRadius: 18,
          display: 'grid',
          placeItems: 'center',
          color: accent.text,
          border: `1px solid ${accent.borderSoft}`,
          background: 'var(--bg-deep)',
          boxShadow: `0 0 32px ${accent.glow}`,
        }}
      >
        <PlanTierIcon code={plan.code} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 32, lineHeight: 1, color: accent.text }}>
          {plan.name}
        </h3>
        {plan.description ? (
          <p style={{ margin: 0, minHeight: 44, fontSize: 13, lineHeight: 1.45, color: 'var(--text-muted)' }}>
            {plan.description}
          </p>
        ) : null}
      </div>

      <div style={{ height: 1, background: 'var(--border-subtle)' }} />

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        {renderPrice(plan.price_cents, locale)}
        <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>
          /{t('billing.planSelector.month', 'mês')}
        </span>
      </div>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {features.map((feature) => (
          <li key={feature.label} style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--text)' }}>
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: 999,
                display: 'grid',
                placeItems: 'center',
                color: '#08101F',
                background: accent.bullet,
                boxShadow: `0 0 18px ${accent.glow}`,
                flexShrink: 0,
              }}
            >
              <CheckIcon />
            </span>
            <span style={{ fontSize: 13, lineHeight: 1.4 }}>{feature.label}</span>
          </li>
        ))}
      </ul>

      {action ? <div style={{ marginTop: 'auto', paddingTop: 8 }}>{action}</div> : null}
    </article>
  )
}

function highlightPlanWord(title: string) {
  const lower = title.toLowerCase()
  const fullPhrase = 'plano ideal'
  const phraseIndex = lower.indexOf(fullPhrase)
  if (phraseIndex !== -1) {
    const before = title.slice(0, phraseIndex)
    const phrase = title.slice(phraseIndex, phraseIndex + fullPhrase.length)
    const after = title.slice(phraseIndex + fullPhrase.length)

    return (
      <>
        {before}
        <span
          style={{
            background: 'linear-gradient(90deg, #22D3EE 0%, #A855F7 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {phrase}
        </span>
        {after}
      </>
    )
  }

  const englishPhrase = 'ideal plan'
  const englishIndex = lower.indexOf(englishPhrase)
  if (englishIndex !== -1) {
    const before = title.slice(0, englishIndex)
    const phrase = title.slice(englishIndex, englishIndex + englishPhrase.length)
    const after = title.slice(englishIndex + englishPhrase.length)

    return (
      <>
        {before}
        <span
          style={{
            background: 'linear-gradient(90deg, #22D3EE 0%, #A855F7 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {phrase}
        </span>
        {after}
      </>
    )
  }

  const index = lower.indexOf('plano')
  if (index === -1) return title

  const before = title.slice(0, index)
  const word = title.slice(index, index + 5)
  const after = title.slice(index + 5)

  return (
    <>
      {before}
      <span
        style={{
          background: 'linear-gradient(90deg, #22D3EE 0%, #A855F7 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        {word}
      </span>
      {after}
    </>
  )
}

function buildPlanFeatures(
  plan: Plan,
  t: ReturnType<typeof useTranslation>['t'],
): PlanFeature[] {
  const code = plan.code.toLowerCase()

  if (code === 'pro') {
    return [
      { label: formatMembers(plan.max_members, t) },
      { label: formatProjects(plan.max_projects, t) },
      { label: t('billing.planSelector.features.proVersioning', 'Versionamento de projeto') },
      { label: t('billing.planSelector.features.proImport', 'Importar projeto: SQL') },
      { label: t('billing.planSelector.features.proExport', 'Exportar projeto: .svg, .png, SQL, relatório de versão') },
      { label: t('billing.planSelector.features.prioritySupport', 'Suporte prioritário') },
    ]
  }

  if (code === 'team') {
    return [
      { label: formatMembers(plan.max_members, t) },
      { label: formatProjects(plan.max_projects, t) },
      { label: t('billing.planSelector.features.teamEverything', 'Tudo do Pro') },
      { label: t('billing.planSelector.features.teamCollaboration', 'Colaboração em equipe') },
      { label: t('billing.planSelector.features.prioritySupport', 'Suporte prioritário') },
    ]
  }

  const features: PlanFeature[] = [
    { label: formatMembers(plan.max_members, t) },
    { label: formatProjects(plan.max_projects, t) },
  ]

  if (plan.allow_version_history) features.push({ label: t('billing.planSelector.features.versionHistory', 'Histórico de versões') })
  if (plan.allow_team_members ?? plan.allows_team) features.push({ label: t('billing.planSelector.features.collaboration', 'Colaboração em equipe') })
  return features
}

function renderPrice(cents: number | null | undefined, locale: string) {
  const value = (cents ?? 0) / 100
  const parts = new Intl.NumberFormat(locale || 'pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).formatToParts(value)

  const integer = parts
    .filter((part) => part.type !== 'fraction' && part.type !== 'decimal')
    .map((part) => part.value)
    .join('')
  const fraction = parts.find((part) => part.type === 'fraction')?.value ?? '00'

  return (
    <span style={{ display: 'inline-flex', alignItems: 'flex-start', color: 'var(--text)' }}>
      <span style={{ fontSize: 50, fontWeight: 800, lineHeight: .92 }}>{integer}</span>
      <span style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1, marginTop: 8 }}>,{fraction}</span>
    </span>
  )
}

function formatMembers(
  limit: number | null | undefined,
  t: ReturnType<typeof useTranslation>['t'],
) {
  if (limit == null) return t('billing.planSelector.limits.unlimitedMembers', 'Usuários ilimitados')
  if (limit <= 1) return t('billing.planSelector.limits.oneMember', '1 usuário')
  return `${t('billing.planSelector.limits.upToPrefix', 'Até')} ${limit} ${t('billing.planSelector.limits.membersSuffix', 'usuários')}`
}

function formatProjects(
  limit: number | null | undefined,
  t: ReturnType<typeof useTranslation>['t'],
) {
  if (limit == null) return t('billing.planSelector.limits.unlimitedProjects', 'Projetos ilimitados')
  if (limit <= 1) return t('billing.planSelector.limits.oneProject', '1 projeto')
  return `${t('billing.planSelector.limits.upToPrefix', 'Até')} ${limit} ${t('billing.planSelector.limits.projectsSuffix', 'projetos')}`
}

function getPlanAccent(code: string, theme: 'light' | 'dark') {
  const normalized = code.toLowerCase()
  const light = theme === 'light'

  if (normalized === 'pro') {
    return {
      text: light ? '#6D28D9' : '#B86BFF',
      bullet: 'linear-gradient(135deg, #A855F7 0%, #8B5CF6 100%)',
      borderSoft: light ? 'rgba(109,40,217,.32)' : 'rgba(184,107,255,.34)',
      borderStrong: light ? 'rgba(109,40,217,.60)' : 'rgba(184,107,255,.68)',
      glow: light ? 'rgba(109,40,217,.14)' : 'rgba(168,85,247,.24)',
    }
  }

  if (normalized === 'team') {
    return {
      text: light ? '#0E7490' : '#22D3EE',
      bullet: 'linear-gradient(135deg, #22D3EE 0%, #06B6D4 100%)',
      borderSoft: light ? 'rgba(14,116,144,.32)' : 'rgba(34,211,238,.28)',
      borderStrong: light ? 'rgba(14,116,144,.60)' : 'rgba(34,211,238,.56)',
      glow: light ? 'rgba(14,116,144,.14)' : 'rgba(34,211,238,.18)',
    }
  }

  return {
    text: light ? '#5B21B6' : '#8B5CF6',
    bullet: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
    borderSoft: light ? 'rgba(91,33,182,.28)' : 'rgba(139,92,246,.24)',
    borderStrong: light ? 'rgba(91,33,182,.50)' : 'rgba(139,92,246,.42)',
    glow: light ? 'rgba(91,33,182,.12)' : 'rgba(99,102,241,.16)',
  }
}

function PlanTierIcon({ code }: { code: string }) {
  const normalized = code.toLowerCase()

  if (normalized === 'pro') {
    return (
      <svg width="34" height="34" viewBox="0 0 34 34" fill="none" aria-hidden="true">
        <path d="M19.2 3.8L8.8 18.4H16L14.8 30.2L25.2 15.6H18L19.2 3.8Z" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" />
      </svg>
    )
  }

  if (normalized === 'team') {
    return (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <circle cx="18" cy="11.5" r="4.6" stroke="currentColor" strokeWidth="2.2" />
        <circle cx="9.8" cy="14.4" r="3.4" stroke="currentColor" strokeWidth="2.2" />
        <circle cx="26.2" cy="14.4" r="3.4" stroke="currentColor" strokeWidth="2.2" />
        <path d="M7.2 28.2C8.6 24.3 12.1 22.2 16.7 22.2H19.3C23.9 22.2 27.4 24.3 28.8 28.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M3.8 28.2C4.5 25.7 6.3 24 8.9 23.1M32.2 28.2C31.5 25.7 29.7 24 27.1 23.1" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    )
  }

  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M18 28.5V18.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M18 18.2C18 14.1 21.2 10.8 25.3 10.8H28.8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M18 18.2C18 14.1 14.8 10.8 10.7 10.8H7.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M11.4 21.2H24.6C24.6 25.2 21.6 28.5 18 28.5C14.4 28.5 11.4 25.2 11.4 21.2Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M10.8 10.8C10.8 8 13.1 5.8 15.8 5.8C17.3 5.8 18.7 6.4 19.6 7.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M25.2 10.8C25.2 8 22.9 5.8 20.2 5.8C18.7 5.8 17.3 6.4 16.4 7.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

function sortPlans(plans: Plan[]) {
  const order = ['free', 'pro', 'team']
  return [...plans].sort((left, right) => {
    const leftIndex = order.indexOf(left.code.toLowerCase())
    const rightIndex = order.indexOf(right.code.toLowerCase())
    return (leftIndex === -1 ? 999 : leftIndex) - (rightIndex === -1 ? 999 : rightIndex)
  })
}
